import {
    Box,
    Card,
    CardContent,
    Chip,
    Checkbox,
    Dialog,
    DialogContent,
    DialogTitle,
    Divider,
    IconButton,
    Typography,
    FormControlLabel,
    Button,
} from "@mui/material";
import { alpha, useTheme } from "@mui/material/styles";
import { CheckCircle, Close, Info, Warning, ArrowBack, ArrowForward } from "@mui/icons-material";
import { blue, green, orange } from "@mui/material/colors";
import dayjs from "dayjs";
import { ConteudoFormatado } from "./Notification/components/ConteudoFormatado";

export const TIPOS_NOTIFICACAO = {
    Funcionalidade: { color: green[600], Icone: CheckCircle, label: 'Nova Funcionalidade' },
    Ajuste: { color: blue[600], Icone: Info, label: 'Alteração' },
    Alerta: { color: orange[800], Icone: Warning, label: 'Alerta Importante' },
};

function nomeDoSistema(formValues, systems) {
    const sistema = formValues?.sistema;

    if (typeof sistema === 'string' && sistema) return sistema;
    if (sistema?.nome) return sistema.nome;

    const id = sistema?.id ?? formValues?.id_sistema;

    if (id == null || id === '') return '';

    return systems?.find((item) => String(item.id) === String(id))?.nome ?? '';
}

function formatarData(data) {
    if (!data) return '';

    const valor = dayjs(data);

    return valor.isValid() ? valor.format('DD/MM/YYYY') : String(data);
}

export const NotificationCard = ({
                                     systems,
                                     formValues,
                                     previewOpen,
                                     setPreviewOpen,
                                     isUserRead = false,
                                     onMarcarComoLido,
                                     currentIndex = 0,
                                     totalNotificacoes = 1,
                                     onProxima,
                                     onAnterior,
                                 }) => {
    const theme = useTheme();

    if (!formValues) return null;

    const tipo = TIPOS_NOTIFICACAO[formValues.tipo] ? formValues.tipo : 'Funcionalidade';
    const config = TIPOS_NOTIFICACAO[tipo];
    const estilo = formValues.estilo ?? {};

    const cor = estilo.cor || config.color;
    const corTextoEtiqueta = theme.palette.getContrastText(cor);
    const rotulo = estilo.rotulo || config.label;
    const sistema = nomeDoSistema(formValues, systems);
    const chips = estilo.chips ?? [];
    const Icone = config.Icone;

    return (
        <Dialog
            open={!!previewOpen}
            onClose={() => setPreviewOpen(false)}
            maxWidth="md"
            fullWidth
            PaperProps={{ sx: { maxHeight: '85vh' } }}
        >
            <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    {!isUserRead ? 'Pré-visualização da Notificação' : 'Notificação'}
                    {totalNotificacoes > 1 && (
                        <Chip label={`${currentIndex + 1} de ${totalNotificacoes}`} size="small" color="primary" />
                    )}
                </Box>
                <IconButton onClick={() => setPreviewOpen(false)} size="small">
                    <Close />
                </IconButton>
            </DialogTitle>

            <DialogContent>
                <Card
                    elevation={0}
                    sx={{
                        border: 1,
                        borderColor: alpha(cor, 0.25),
                        borderTop: `5px solid ${cor}`,
                        borderRadius: 2,
                        bgcolor: alpha(cor, 0.04),
                        mb: 2,
                    }}
                >
                    <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
                        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
                            <Box
                                sx={{
                                    width: 44,
                                    height: 44,
                                    flexShrink: 0,
                                    borderRadius: '50%',
                                    display: 'grid',
                                    placeItems: 'center',
                                    bgcolor: alpha(cor, 0.12),
                                    color: cor,
                                    fontSize: 24,
                                    lineHeight: 1,
                                }}
                            >
                                {estilo.emoji ? estilo.emoji : <Icone />}
                            </Box>

                            <Box sx={{ flex: 1, minWidth: 0 }}>
                                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 1, mb: 1 }}>
                                    <Chip
                                        label={rotulo}
                                        size="small"
                                        sx={{ bgcolor: cor, color: corTextoEtiqueta, fontWeight: 700 }}
                                    />
                                    <Typography variant="caption" color="text.secondary">
                                        {formatarData(formValues.data)}
                                    </Typography>
                                </Box>

                                <Typography variant="h6" sx={{ fontWeight: 700, mb: 1, color: cor, lineHeight: 1.3 }}>
                                    {formValues.nome || 'Nome da Alteração'}
                                </Typography>

                                {(sistema || formValues.versao || chips.length > 0) && (
                                    <Box sx={{ display: 'flex', gap: 1, mb: 2, flexWrap: 'wrap' }}>
                                        {sistema && (
                                            <Chip label={sistema} size="small" variant="outlined" sx={{ borderColor: alpha(cor, 0.4) }} />
                                        )}
                                        {formValues.versao && (
                                            <Chip label={`v${formValues.versao}`} size="small" variant="outlined" sx={{ borderColor: alpha(cor, 0.4) }} />
                                        )}
                                        {chips.map((chip, indice) => (
                                            <Chip
                                                key={`${chip.label}-${indice}`}
                                                label={chip.label}
                                                size="small"
                                                sx={{
                                                    bgcolor: chip.cor,
                                                    color: theme.palette.getContrastText(chip.cor),
                                                    fontWeight: 600,
                                                }}
                                            />
                                        ))}
                                    </Box>
                                )}

                                <Divider sx={{ mb: 2, borderColor: alpha(cor, 0.2) }} />

                                <ConteudoFormatado
                                    html={formValues.descricao}
                                    vazio="Descrição da alteração..."
                                    sx={{ mb: 2, color: 'text.primary' }}
                                />

                                {formValues.observacao && (
                                    <Box
                                        sx={{
                                            bgcolor: 'background.paper',
                                            p: 1.5,
                                            borderRadius: 1,
                                            borderLeft: `3px solid ${cor}`,
                                        }}
                                    >
                                        <Typography variant="caption" sx={{ fontWeight: 700, display: 'block', mb: 0.5 }}>
                                            Observação
                                        </Typography>
                                        <ConteudoFormatado html={formValues.observacao} sx={{ color: 'text.secondary' }} />
                                    </Box>
                                )}

                                {isUserRead && (
                                    <FormControlLabel
                                        control={
                                            <Checkbox
                                                checked={!!formValues.lido}
                                                onChange={onMarcarComoLido}
                                                sx={{ color: cor, '&.Mui-checked': { color: cor } }}
                                            />
                                        }
                                        label="Marcar como lido"
                                        sx={{ mt: 2 }}
                                    />
                                )}
                            </Box>
                        </Box>
                    </CardContent>
                </Card>

                {totalNotificacoes > 1 && (
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 2 }}>
                        <Button startIcon={<ArrowBack />} onClick={onAnterior} disabled={currentIndex === 0} variant="outlined">
                            Anterior
                        </Button>
                        <Button endIcon={<ArrowForward />} onClick={onProxima} variant="contained">
                            {currentIndex < totalNotificacoes - 1 ? 'Próxima' : 'Fechar'}
                        </Button>
                    </Box>
                )}

                {!isUserRead && (
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', textAlign: 'center', mt: 2 }}>
                        Esta é uma prévia de como a notificação será exibida para os usuários
                    </Typography>
                )}
            </DialogContent>
        </Dialog>
    );
};